/**
 * This is a NotifyMe component.This component will used to notify the user through email when the product is available.
 *
 * @module views/components/NotifyMeV2
 * @memberof -Common
 */
import './NotifyMeV2.scss';

import React, { useCallback, useEffect, useMemo, useRef } from 'react';

import { Form, Formik } from 'formik';
import PropTypes from 'prop-types';
import * as Yup from 'yup';

import Button from '@ulta/core/components/Button/Button';
import CheckboxToggle from '@ulta/core/components/CheckboxToggle/CheckboxToggle';
import Icon from '@ulta/core/components/Icon/Icon';
import InputField from '@ulta/core/components/InputField/InputField';
import Markdown from '@ulta/core/components/Markdown/Markdown';
import RadioButton from '@ulta/core/components/RadioButton_Huge/RadioButton_Huge';
import Text from '@ulta/core/components/Text/Text';
import { isServer } from '@ulta/core/utils/device_detection/device_detection';
import { handleEmptyObjects, isEmptyObject } from '@ulta/core/utils/handleEmptyObjects/handleEmptyObjects';

import constants from '@ulta/utils/constants/constants';
import { addYupCustomRegex } from '@ulta/utils/Validations/Validations';

import * as utils from './NotifyMeV2';

/**
 * Represents a NotifyMe component
 *
 * @method
 * @param {NotifyMeV2Props} props - React properties passed from composition
 * @returns NotifyMeV2
 */
export const NotifyMeV2 = function( props ){
  const {
    email = {},
    phone = {},
    notifyIcon,
    notifyInfo,
    notifyMeMessage,
    notifyMeAction,
    notifyMeDisabled,
    signupDescription,
    signupRequiredLabel,
    infoLabel,
    invokeMutation
  } = props;
  const formikRef = useRef();
  const onSubmit = useCallback(
    utils.composeOnSubmit( { props }, { invokeMutation } ),
    [props, invokeMutation]
  );

  const availableOptions = [
    phone?.name ? { ...phone, optionType: 'phone' } : null,
    email?.name ? { ...email, optionType: 'email' } : null
  ].filter( Boolean );

  useEffect( () => {
    if( infoLabel?.message && formikRef.current ){
      const { notificationType } = formikRef.current.values;
      formikRef.current.setFieldError( notificationType, infoLabel.message );
      formikRef.current.setFieldValue( 'signup', false );
    }
  }, [infoLabel] );

  useEffect( () => {
    if( formikRef.current ){
      formikRef.current.setFieldValue( email?.name, email?.value || '' );
      formikRef.current.setFieldValue( phone?.name, phone?.value || '' );
    }
  }, [email?.value, phone?.value, notifyInfo, notifyMeDisabled] );


  const validationSchema = useMemo( () => utils.getValidationSchema( { email, phone } ), [email, phone] );

  if( !notifyMeAction || ( isEmptyObject( email ) && isEmptyObject( phone ) && !notifyInfo ) ){
    return null;
  }

  return (
    <div className='NotifyMe'>
      <Formik
        initialValues={ {
          [email?.name]: email?.value || '',
          [phone?.name]: phone?.value || '',
          notificationType: phone?.name || email?.name,
          signup: false
        } }
        onSubmit={ onSubmit }
        enableReinitialize={ true }
        validationSchema={ validationSchema }
        innerRef={ formikRef }
        validateOnChange={ false }
      >
        { ( { values, handleChange, handleBlur, validateForm } ) => (
          <Form>
            <Text
              htmlTag='p'
              textStyle='body-2'
            >
              { notifyMeMessage }
            </Text>
            { notifyInfo &&
              <div className='NotifyMe__Info'>
                <span className='icon'>
                  <Icon
                    size='m'
                    name={ notifyIcon }
                    aria-hidden={ true }
                  />
                </span>
                <Text
                  htmlTag='p'
                  textStyle='body-sm'
                  color='neutral-600'
                >
                  { notifyInfo }
                </Text>
              </div> }
            { !notifyMeDisabled && <div>
              <fieldset>
                <legend className='sr-only'>{ notifyMeMessage }</legend>
                { /* Case 1: Multiple options → radio buttons */ }
                { availableOptions.length > 1 && (
                  <div className='NotifyMe__radioContainer'>
                    { availableOptions.map( ( opt ) => (
                      <RadioButton
                        key={ opt.name }
                        name='notificationType'
                        id={ opt.name }
                        value={ opt.name }
                        isChecked={ values.notificationType === opt.name }
                        isDisabled={ false }
                        onChange={ handleChange }
                        onBlur={ handleBlur }
                        className='InputFieldRadio'
                      >
                        { opt.label }
                      </RadioButton>
                    ) ) }
                  </div>
                ) }

                { /* Case 2: Single option → just a label */ }
                { availableOptions.length === 1 && (
                  <div className='NotifyMe__label'>
                    <Text htmlTag='p' color='default' textStyle='body-lg'>
                      { availableOptions[0].label }
                    </Text>
                  </div>
                ) }

              </fieldset>
              <div className='NotifyMe__action'>
                { availableOptions.map(
                  ( opt ) =>
                    values.notificationType === opt.name && (
                      <InputField
                        key={ opt.name }
                        name={ opt.name }
                        label={ opt.name }
                        type={ opt.type }
                        autoCorrect='on'
                        spellCheck={ true }
                        showValidationMessage={ true }
                        placeholderLabel={ opt.placeholderLabel || '' }
                        formatter={
                          opt.optionType === 'phone' ?
                            constants.FORMATTER_PATTERNS.PHONE_NO_FORMATTER :
                            undefined
                        }
                        autoComplete={ opt.optionType === 'phone' ? 'tel' : 'email' }
                      />
                    )
                ) }
              </div>
              { /* Signup checkbox (phone only) */ }
              { ( signupRequiredLabel && values.notificationType === phone?.name ) &&
              <div className='NotifyMe__action'>
                <CheckboxToggle
                  id='signup'
                  name='signup'
                  toggleButton={ false }
                  checked={ values.signup }
                  onChange={ () => {
                    validateForm();
                  } }
                  onBlur={ handleBlur }
                  labelPositionLeft={ false }
                >
                  <Text htmlTag='p'
                    textStyle='body'
                  >
                    { signupRequiredLabel?.label }
                  </Text>
                </CheckboxToggle>
                <div className='NotifyMe__SignupText'>
                  <Markdown
                    bodyStyle='body-3'
                    bodyColor='neutral-700'
                    cmsMarkdownContent={ signupDescription }
                  />
                </div>
              </div> }

              <Button
                type='submit'
                size='compact'
                label={ notifyMeAction.label }
                disabled={ ( values.notificationType === phone?.name ? !values.signup : false ) }
              />
            </div> }
          </Form>
        ) }
      </Formik>
    </div>
  );
};

export const composeOnSubmit = ( data, methods ) => ( values ) => {
  const { props } = data;
  const { notifyMeAction, phone, email, fulfillmentType } = props || {};
  const { notificationType } = values || {};
  const emailAddress = values ? values[email?.name] : '';
  const phoneNumber = values ? values[phone?.name] : '';
  const { graphql, customHeaders, params } = notifyMeAction || {};
  const { invokeMutation } = methods || {};
  if( ( !emailAddress && !phoneNumber ) || !graphql || !invokeMutation ){
    return;
  }
  let notifyMeParams = {};
  if( notificationType === phone?.name ){

    notifyMeParams = {
      phone: phoneNumber?.replace( '(', '' ).replace( ') ', '-' )
    };
  }
  else {
    notifyMeParams = {
      notifyEmailAddress: emailAddress
    };
  }
  {
    invokeMutation( {
      graphql,
      customHeaders,
      params,
      variables: {
        moduleParams: { ...notifyMeParams, fulfillmentType },
        url: { path: !isServer() && global.location.pathname }
      }
    } );
  }
};

/**
 * A handler function for manually setting form fields
 * @method getValidationSchema
 * @param {object} data - Contains input fields and their Amplience configurations
 * @returns {object} validationSchema - Returns Object containing Yup validations for each passed in field.
 */
export const getValidationSchema = ( data ) => {
  const { email, phone } = handleEmptyObjects( data );

  addYupCustomRegex( { schemaType: Yup.string, name: email?.name, validations: email?.validations } );
  addYupCustomRegex( { schemaType: Yup.string, name: phone?.name, validations: phone?.validations } );

  return Yup.object( {
    [phone?.name]: Yup.string().when( 'notificationType', {
      is: phone?.name,
      then: ( schema ) => schema.required( phone?.validations?.[0]?.message ).matches( new RegExp( phone?.validations?.[0]?.regex ), phone?.validations?.[0]?.message ),
      otherwise: ( schema ) => schema.notRequired()
    } ),
    [email?.name]: Yup.string().when( 'notificationType', {
      is: email?.name,
      then: ( schema ) => schema.required( email?.validations?.[0]?.message ).matches( new RegExp( email?.validations?.[0]?.regex ), email?.validations?.[0]?.message ),
      otherwise: ( schema ) => schema.notRequired()
    } )
  } );
};

/**
 * Property type definitions
 *
 * @typedef NotifyMeV2Props
 * @type {object}
 * @property {object} email - Email input field to be displayed
 * @property {string} notifyMeMessage - Label to be displayed
 * @property {object} notifyMeAction - Label to be displayed for button
 * @property {boolean} notifyMeDisabled - Flag for the disabled notify me
 * @property {boolean} bookmarked - True/False value for the bookmarked
 * @property {object} bookmarkAction - Label to be displayed
 * @property {object} removeBookmarkAction - The action that should be executed when user removes a product from bookmarks consisting of Label and Graphql
 * @property {string} skuId - Sets skuId value
 * @property {string} productId - Sets productId value
 */
export const propTypes = {
  email: PropTypes.object,
  phone: PropTypes.object,
  notifyIcon: PropTypes.string,
  notifyInfo: PropTypes.string,
  notifyMeMessage: PropTypes.string,
  notifyMeAction: PropTypes.shape( {
    label: PropTypes.string,
    graphql: PropTypes.string
  } ),
  notifyMeDisabled: PropTypes.bool,
  bookmarked: PropTypes.bool,
  bookmarkAction: PropTypes.shape( {
    label: PropTypes.string,
    graphql: PropTypes.string
  } ),
  removeBookmarkAction: PropTypes.shape( {
    label: PropTypes.string,
    graphql: PropTypes.string
  } ),
  skuId: PropTypes.string,
  productId: PropTypes.string,
  signupDescription: PropTypes.string,
  invokeMutation: PropTypes.func
};

NotifyMeV2.propTypes = propTypes;

export default NotifyMeV2;
